import React, { useCallback, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink, Navigate, useLocation, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutDashboard, Users, BarChart2, BookOpen, ClipboardCheck, Settings2, LogOut, Menu, CalendarDays, GraduationCap, MessageCircle, Video, ChevronsLeft, ChevronsRight, FileBadge, Fingerprint, Briefcase, ChevronDown, ChevronRight, Zap, Calendar, Wallet, Tag, Home, Mail, FileText, Bus } from 'lucide-react';
import Dashboard        from './components/Dashboard';
import StudentList      from './components/StudentList';
import Reports          from './components/Reports';
import Login            from './components/Login';
import AdminPanel       from './components/AdminPanel';
import OperationsPanel, { OPERATIONS_SECTIONS } from './components/OperationsPanel';
import ChangePassword   from './components/ChangePassword';
import Exams            from './components/Exams';
import TransportSection from './components/TransportSection';
import DayBook          from './components/DayBook';
import ProgressCard     from './components/ProgressCard';
import StudentFeeInfo   from './components/StudentFeeInfo';
import WhatsAppBulk     from './components/WhatsAppBulk';
import Chat             from './components/Chat';
import Meeting          from './components/Meeting';
import ParentDashboard  from './components/ParentDashboard';
import PwaInstallPrompt from './components/PwaInstallPrompt';
import DriverLogin      from './components/DriverLogin';
import DriverMode       from './components/DriverMode';
import Messages         from './components/Messages';
import Certificates          from './components/Certificates';
import BiometricVerification from './components/BiometricVerification';
import EmployeeBiometric     from './components/EmployeeBiometric';
import StudentBiometric      from './components/StudentBiometric';
import Employees              from './components/Employees';
import AdmissionFormPrint     from './components/AdmissionFormPrint';
import { YearProvider, useYear } from './context/YearContext';
import { schoolLogoAPI, authAPI } from './services/api';
import { usePushNotifications } from './hooks/usePushNotifications';
import './App.css';

// ── Tab-based access permissions ────────────────────────────────────────────
const ALL_TABS = [
  'dashboard', 'admission-form', 'students', 'reports', 'daybook', 'exams', 'transport', 'operations',
  'certificates', 'biometric', 'employee-biometric', 'student-biometric', 'employees',
  'whatsapp', 'chat', 'messages', 'meeting',
];

function canAccess(allowedTabs, tab) {
  if (!Array.isArray(allowedTabs)) return true;
  if (allowedTabs.includes(tab)) return true;
  // 'transport' is a new top-level tab (previously lived under Operations).
  // If a user already has 'operations' access but their saved permissions
  // haven't been updated to include 'transport' yet, grant it automatically.
  if (tab === 'transport' && allowedTabs.includes('operations')) return true;
  return false;
}

// Generate academic years from 2022-23 to 2033-34
function yearOptions() {
  const years = [];
  for (let y = 2022; y <= 2033; y++) {
    years.push(`${y}-${String(y + 1).slice(-2)}`);
  }
  return years;
}

// ── Operations sub-nav (each item is a sub-route under /operations) ──────────
const OPERATIONS_SUBNAV_ICONS = {
  bulk:            <Zap      size={15} />,
  monthly:         <Calendar size={15} />,
  'fee-templates': <Wallet   size={15} />,
  'fee-types':     <Tag      size={15} />,
  hostel:          <Home     size={15} />,
  'fee-manage':    <Wallet   size={15} />,
  users:           <Users    size={15} />,
  messages:        <Mail     size={15} />,
};
const _opSectionByKey = Object.fromEntries(OPERATIONS_SECTIONS.map((s) => [s.key, s]));
const _opEntry = (key) => {
  const s = _opSectionByKey[key];
  if (!s) return null;
  return { to: `/operations/${s.key}`, label: s.label, icon: OPERATIONS_SUBNAV_ICONS[s.key] || null };
};
// Flat sub-nav. "Fee Management" is a single page that stacks the 4 fee sections.
const OPERATIONS_SUBNAV = [
  _opEntry('fee-manage'),
  _opEntry('bulk'),
  _opEntry('users'),
  _opEntry('messages'),
].filter(Boolean);

// ── Sidebar nav items ─────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: 'MAIN MENU',
    items: [
      { tab: 'dashboard',          to: '/',                   end: true, label: 'Dashboard',          icon: <LayoutDashboard size={18} /> },
      { tab: 'admission-form',     to: '/admission-form',                label: 'Admission Form',     icon: <FileText        size={18} /> },
      { tab: 'students',           to: '/students',                      label: 'Students',           icon: <Users           size={18} /> },
      { tab: 'reports',            to: '/reports',                       label: 'Accounts',           icon: <BarChart2       size={18} /> },
      { tab: 'certificates',       to: '/certificates',                  label: 'Certificates',       icon: <FileBadge       size={18} /> },
      { tab: 'employees',          to: '/employees',                     label: 'Employees',          icon: <Briefcase       size={18} /> },
      { tab: 'daybook',            to: '/daybook',                       label: 'Day Book',           icon: <BookOpen        size={18} /> },
      { tab: 'exams',              to: '/exams',                         label: 'Exams',              icon: <ClipboardCheck  size={18} /> },
      { tab: 'transport',          to: '/transport',                     label: 'Buses & Routes',     icon: <Bus             size={18} /> },
      { tab: 'operations',         to: '/operations',                    label: 'Operations',         icon: <Settings2       size={18} />, children: OPERATIONS_SUBNAV },
      { tab: 'employee-biometric', to: '/employee-biometric',            label: 'Employee Biometric', icon: <Fingerprint     size={18} /> },
      { tab: 'student-biometric',  to: '/student-biometric',             label: 'Student Biometric',  icon: <Fingerprint     size={18} /> },
    ],
  },
];

const WHATSAPP_NAV = {
  to: '/whatsapp', label: 'WhatsApp',
  icon: (
    <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
    </svg>
  ),
};

// ── Page title map ─────────────────────────────────────────────────────────────
function usePageTitle() {
  const location = useLocation();
  const titles = {
    '/': 'Dashboard',
    '/admission-form': 'Admission Form',
    '/students': 'Students',
    '/reports': 'Accounts',
    '/daybook': 'Day Book',
    '/exams': 'Exams',
    '/transport': 'Buses & Routes',
    '/operations': 'Operations',
    '/whatsapp': 'WhatsApp',
    '/chat': 'Messages',
    '/meeting': 'Meeting',
    '/certificates': 'Certificates',
    '/biometric':             'Biometric Verification',
    '/employee-biometric':    'Employee Biometric',
    '/student-biometric':     'Student Biometric',
    '/employees':             'Employees',
  };
  // Sub-routes under /operations/<section>
  if (location.pathname.startsWith('/operations/')) {
    const key  = location.pathname.split('/')[2];
    const meta = OPERATIONS_SECTIONS.find((s) => s.key === key);
    return meta ? `Operations · ${meta.label}` : 'Operations';
  }
  return titles[location.pathname] || 'Dashboard';
}

// ── Sidebar ────────────────────────────────────────────────────────────────────
function Sidebar({ allowedTabs, whatsappEnabled, lockedTabs = [], sidebarOpen, onClose, schoolName, staffFullName, schoolLogoUrl, onLogout, collapsed, onToggleCollapse }) {
  const isLocked = (tab) => lockedTabs.includes(tab);  const location = useLocation();
  // Track which expandable groups are open. Auto-open Operations when on a sub-route.
  const [openGroups, setOpenGroups] = useState(() => ({
    operations: location.pathname.startsWith('/operations'),
  }));
  useEffect(() => {
    if (location.pathname.startsWith('/operations')) {
      setOpenGroups((g) => ({ ...g, operations: true }));
    }
  }, [location.pathname]);
  const toggleGroup = (tab) => setOpenGroups((g) => ({ ...g, [tab]: !g[tab] }));  return (
    <>
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={onClose} aria-hidden="true" />
      )}
      <aside className={`sidebar${sidebarOpen ? ' sidebar-open' : ''}${collapsed ? ' sidebar-collapsed' : ''}`}>
        {/* Brand */}
        <div className="sidebar-brand">
          <div className="sidebar-logo-wrap">
            {schoolLogoUrl ? (
              <img src={schoolLogoUrl} alt="Logo" className="sidebar-school-logo"
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
            ) : null}
            <div className="sidebar-logo-fallback" style={{ display: schoolLogoUrl ? 'none' : 'flex' }}>
              <GraduationCap size={22} />
            </div>
          </div>
          {!collapsed && (
            <div className="sidebar-brand-text">
              <span className="sidebar-brand-name">{schoolName || 'School ERP'}</span>
              {staffFullName
                ? <span className="sidebar-brand-sub">{staffFullName}</span>
                : <span className="sidebar-brand-sub">Fee Management</span>
              }
            </div>
          )}
          <button
            className="sidebar-collapse-btn"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          {NAV_GROUPS.map((group, gi) => {
            const visibleItems = group.items.filter(item => canAccess(allowedTabs, item.tab));
            if (visibleItems.length === 0) return null;
            return (
              <div key={group.label}>
                {!collapsed && <div className="sidebar-nav-label" style={gi > 0 ? { marginTop: '1rem' } : undefined}>{group.label}</div>}
                {collapsed && gi > 0 && <div className="sidebar-nav-divider" />}
                {visibleItems.map(item => {
                  if (isLocked(item.tab)) {
                    return (
                      <div
                        key={item.to}
                        className="sidebar-link sidebar-link-locked"
                        title={collapsed ? `${item.label} (Locked)` : 'This feature is locked. Contact your administrator.'}
                      >
                        <span className="sidebar-link-icon">{item.icon}</span>
                        {!collapsed && <span className="sidebar-link-label">{item.label}</span>}
                        {!collapsed && <span className="sidebar-lock-icon">🔒</span>}
                        {collapsed && <span className="sidebar-lock-icon-collapsed">🔒</span>}
                      </div>
                    );
                  }
                  // Expandable group (has children) — click to toggle sub-nav.
                  if (item.children && item.children.length && !collapsed) {
                    const isOpen   = !!openGroups[item.tab];
                    const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                    return (
                      <div key={item.to} className="sidebar-group">
                        <button
                          type="button"
                          className={`sidebar-link sidebar-link-group${isActive ? ' sidebar-link-active' : ''}`}
                          onClick={() => toggleGroup(item.tab)}
                          aria-expanded={isOpen}
                        >
                          <span className="sidebar-link-icon">{item.icon}</span>
                          <span className="sidebar-link-label">{item.label}</span>
                          <span className="sidebar-group-chevron">
                            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                          </span>
                        </button>
                        {isOpen && (
                          <div className="sidebar-sublinks">
                            {item.children.map((child) => {
                              // Nested children (one extra level) — e.g. Monthly / Hostel / Fee Types / Fee Templates under Fee Manage.
                              if (child.children && child.children.length) {
                                const childKey   = `${item.tab}::${child.key || child.to || child.label}`;
                                const childOpen  = openGroups[childKey] !== false; // default open
                                const childPaths = child.children.map((g) => g.to);
                                const childActive = (child.to && (location.pathname === child.to || location.pathname.startsWith(child.to + '/'))) || childPaths.includes(location.pathname);
                                // Header is a NavLink when child.to exists, otherwise a plain button.
                                const headerCommon = (
                                  <>
                                    <span className="sidebar-link-icon">{child.icon}</span>
                                    <span className="sidebar-link-label">{child.label}</span>
                                  </>
                                );
                                return (
                                  <div key={child.key || child.to || child.label} className="sidebar-subgroup">
                                    <div className="sidebar-subgroup-header">
                                      {child.to ? (
                                        <NavLink
                                          to={child.to}
                                          className={({ isActive: ca }) => `sidebar-link sidebar-sublink sidebar-sublink-parent${ca ? ' sidebar-link-active' : ''}`}
                                          onClick={onClose}
                                          style={{ flex: 1 }}
                                        >
                                          {headerCommon}
                                        </NavLink>
                                      ) : (
                                        <button
                                          type="button"
                                          className={`sidebar-link sidebar-sublink sidebar-sublink-parent sidebar-sublink-header${childActive ? ' sidebar-link-active' : ''}`}
                                          onClick={() => toggleGroup(childKey)}
                                          style={{ flex: 1 }}
                                          aria-expanded={childOpen}
                                        >
                                          {headerCommon}
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="sidebar-subgroup-toggle"
                                        onClick={() => toggleGroup(childKey)}
                                        aria-expanded={childOpen}
                                        aria-label={childOpen ? 'Collapse' : 'Expand'}
                                      >
                                        {childOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                      </button>
                                    </div>
                                    {childOpen && (
                                      <div className="sidebar-sublinks sidebar-sublinks-nested">
                                        {child.children.map((grand) => (
                                          <NavLink
                                            key={grand.to}
                                            to={grand.to}
                                            className={({ isActive: ga }) => `sidebar-link sidebar-sublink sidebar-sublink-grand${ga ? ' sidebar-link-active' : ''}`}
                                            onClick={onClose}
                                          >
                                            <span className="sidebar-link-icon">{grand.icon}</span>
                                            <span className="sidebar-link-label">{grand.label}</span>
                                          </NavLink>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              }
                              return (
                                <NavLink
                                  key={child.to}
                                  to={child.to}
                                  className={({ isActive: childActive }) => `sidebar-link sidebar-sublink${childActive ? ' sidebar-link-active' : ''}`}
                                  onClick={onClose}
                                >
                                  <span className="sidebar-link-icon">{child.icon}</span>
                                  <span className="sidebar-link-label">{child.label}</span>
                                </NavLink>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) => `sidebar-link${isActive ? ' sidebar-link-active' : ''}`}
                      onClick={onClose}
                      title={collapsed ? item.label : undefined}
                    >
                      <span className="sidebar-link-icon">{item.icon}</span>
                      {!collapsed && <span className="sidebar-link-label">{item.label}</span>}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}

          {!collapsed && <div className="sidebar-nav-label" style={{ marginTop: '1rem' }}>COMMUNICATION</div>}
          {collapsed && <div className="sidebar-nav-divider" />}
          {canAccess(allowedTabs, 'whatsapp') && (
            isLocked('whatsapp') || !whatsappEnabled ? (
              <div
                className="sidebar-link sidebar-link-locked sidebar-link-whatsapp"
                title={collapsed ? 'WhatsApp (Locked)' : 'This feature is locked. Contact your administrator.'}
              >
                <span className="sidebar-link-icon">{WHATSAPP_NAV.icon}</span>
                {!collapsed && <span className="sidebar-link-label">{WHATSAPP_NAV.label}</span>}
                {!collapsed && <span className="sidebar-lock-icon">🔒</span>}
                {collapsed && <span className="sidebar-lock-icon-collapsed">🔒</span>}
              </div>
            ) : (
              <NavLink
                to={WHATSAPP_NAV.to}
                className={({ isActive }) => `sidebar-link sidebar-link-whatsapp${isActive ? ' sidebar-link-active' : ''}`}
                onClick={onClose}
                title={collapsed ? 'WhatsApp' : undefined}
              >
                <span className="sidebar-link-icon">{WHATSAPP_NAV.icon}</span>
                {!collapsed && <span className="sidebar-link-label">{WHATSAPP_NAV.label}</span>}
              </NavLink>
            )
          )}
          {canAccess(allowedTabs, 'chat') && (
            <NavLink
              to="/chat"
              className={({ isActive }) => `sidebar-link${isActive ? ' sidebar-link-active' : ''}`}
              onClick={onClose}
              title={collapsed ? 'Messages' : undefined}
            >
              <span className="sidebar-link-icon"><MessageCircle size={18} /></span>
              {!collapsed && <span className="sidebar-link-label">Messages</span>}
            </NavLink>
          )}
          {canAccess(allowedTabs, 'messages') && (
            <NavLink
              to="/messages"
              className={({ isActive }) => `sidebar-link${isActive ? ' sidebar-link-active' : ''}`}
              onClick={onClose}
              title={collapsed ? 'Bulk Messages' : undefined}
            >
              <span className="sidebar-link-icon">💬</span>
              {!collapsed && <span className="sidebar-link-label">Bulk Messages</span>}
            </NavLink>
          )}
          {canAccess(allowedTabs, 'meeting') && (
            <NavLink
              to="/meeting"
              className={({ isActive }) => `sidebar-link${isActive ? ' sidebar-link-active' : ''}`}
              onClick={onClose}
              title={collapsed ? 'Meeting' : undefined}
            >
              <span className="sidebar-link-icon"><Video size={18} /></span>
              {!collapsed && <span className="sidebar-link-label">Meeting</span>}
            </NavLink>
          )}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          <button className="sidebar-logout-btn" onClick={onLogout} title="Logout">
            <LogOut size={16} />
            {!collapsed && <span>Logout</span>}
          </button>
          {!collapsed && <div className="sidebar-footer-copy">© {new Date().getFullYear()} School ERP</div>}
        </div>
      </aside>
    </>
  );
}

// ── Top Header ─────────────────────────────────────────────────────────────────
function TopHeader({ onMenuToggle }) {
  return (
    <header className="top-header top-header--minimal">
      <button className="menu-toggle" onClick={onMenuToggle} aria-label="Toggle sidebar">
        <Menu size={20} />
      </button>
    </header>
  );
}

// ── Page transition ───────────────────────────────────────────────────────────
const pageVariants = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0,  transition: { duration: 0.22, ease: [0.4, 0, 0.2, 1] } },
  exit:    { opacity: 0, y: -8, transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] } },
};

function PageTransition({ children }) {
  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{ height: '100%' }}
    >
      {children}
    </motion.div>
  );
}

// Wrapper that reads :section from the URL and passes it to OperationsPanel.
function OperationsRoute() {
  const { section } = useParams();
  return <OperationsPanel section={section} />;
}

function AnimatedRoutes({ allowedTabs, whatsappEnabled, lockedTabs = [], schoolName, schoolLocation, schoolTagline, contactName, onContactNameChange, schoolLogoUrl }) {
  const isLocked = (tab) => lockedTabs.includes(tab);
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={
          <PageTransition>
            {canAccess(allowedTabs, 'dashboard')
              ? <Dashboard schoolName={schoolName} schoolLocation={schoolLocation} schoolTagline={schoolTagline} contactName={contactName} schoolLogoUrl={schoolLogoUrl} onContactNameChange={onContactNameChange} />
              : <Navigate to="/students" replace />}
          </PageTransition>
        } />
        <Route path="/admission-form" element={
          <PageTransition>
            {canAccess(allowedTabs, 'admission-form')
              ? <AdmissionFormPrint schoolName={schoolName} schoolLogoUrl={schoolLogoUrl} />
              : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/students" element={
          <PageTransition>
            {canAccess(allowedTabs, 'students')
              ? <StudentList schoolName={schoolName} schoolLogoUrl={schoolLogoUrl} />
              : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/reports" element={
          <PageTransition>
            {canAccess(allowedTabs, 'reports')
              ? <Reports schoolName={schoolName} />
              : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/daybook" element={
          <PageTransition>
            {canAccess(allowedTabs, 'daybook') ? <DayBook /> : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/exams" element={
          <PageTransition>
            {canAccess(allowedTabs, 'exams') ? <Exams /> : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/transport" element={
          <PageTransition>
            {canAccess(allowedTabs, 'transport') ? (
              <div className="page">
                <div className="page-header" style={{ marginBottom: '1.5rem' }}>
                  <h1 className="page-title">Buses &amp; Routes</h1>
                </div>
                <TransportSection />
              </div>
            ) : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/operations" element={
          <PageTransition>
            {canAccess(allowedTabs, 'operations') ? <Navigate to="/operations/fee-manage" replace /> : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/operations/:section" element={
          <PageTransition>
            {canAccess(allowedTabs, 'operations') ? <OperationsRoute /> : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/certificates" element={
          <PageTransition>
            {canAccess(allowedTabs, 'certificates')
              ? <Certificates schoolName={schoolName} schoolLogoUrl={schoolLogoUrl} />
              : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/biometric" element={
          <PageTransition>
            {canAccess(allowedTabs, 'biometric')
              ? <BiometricVerification />
              : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/employee-biometric" element={
          <PageTransition>
            {canAccess(allowedTabs, 'employee-biometric') && !isLocked('employee-biometric')
              ? <EmployeeBiometric />
              : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/student-biometric" element={
          <PageTransition>
            {canAccess(allowedTabs, 'student-biometric') && !isLocked('student-biometric')
              ? <StudentBiometric />
              : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/employees" element={
          <PageTransition>
            {canAccess(allowedTabs, 'employees')
              ? <Employees />
              : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/whatsapp" element={
          <PageTransition>
            {canAccess(allowedTabs, 'whatsapp') && !isLocked('whatsapp') && whatsappEnabled
              ? <WhatsAppBulk schoolName={schoolName} />
              : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/chat" element={
          <PageTransition>
            {canAccess(allowedTabs, 'chat') ? <Chat /> : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/messages" element={
          <PageTransition>
            {canAccess(allowedTabs, 'messages') ? <Messages schoolName={schoolName} /> : <Navigate to="/" replace />}
          </PageTransition>
        } />
        <Route path="/meeting" element={
          <PageTransition>
            {canAccess(allowedTabs, 'meeting') ? <Meeting /> : <Navigate to="/" replace />}
          </PageTransition>
        } />
      </Routes>
    </AnimatePresence>
  );
}

// ── Layout (sidebar + header + content) ───────────────────────────────────────
function AppLayout({ children, onLogout, schoolName, staffFullName, allowedTabs, whatsappEnabled, lockedTabs, schoolLogoUrl }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Lock body scroll on iOS when the sidebar is open so background doesn't scroll through overlay
  useEffect(() => {
    if (sidebarOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) window.scrollTo(0, -parseInt(scrollY, 10));
    }
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [sidebarOpen]);

  return (
    <div className={`app-layout${sidebarCollapsed ? ' sidebar-is-collapsed' : ''}`}>
      <Sidebar
        allowedTabs={allowedTabs}
        whatsappEnabled={whatsappEnabled}
        lockedTabs={lockedTabs}
        sidebarOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        schoolName={schoolName}
        staffFullName={staffFullName}
        schoolLogoUrl={schoolLogoUrl}
        onLogout={onLogout}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
      />
      <div className="app-main">
        <TopHeader onMenuToggle={() => setSidebarOpen((v) => !v)} />
        <main className="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}

export default function App() {
  const [loggedIn, setLoggedIn] = useState(
    () => !!sessionStorage.getItem('gs_token')
  );
  const [schoolName, setSchoolName] = useState(
    () => sessionStorage.getItem('gs_school_name') || ''
  );
  const [schoolLocation, setSchoolLocation] = useState(
    () => sessionStorage.getItem('gs_school_location') || ''
  );
  const [schoolTagline, setSchoolTagline] = useState(
    () => sessionStorage.getItem('gs_school_tagline') || ''
  );
  const [schoolLogoUrl, setSchoolLogoUrl] = useState(
    () => sessionStorage.getItem('gs_school_logo_url') || ''
  );
  const [contactName, setContactName] = useState(
    () => sessionStorage.getItem('gs_contact_name') || ''
  );
  const [staffFullName, setStaffFullName] = useState(
    () => sessionStorage.getItem('gs_staff_full_name') || ''
  );
  const [allowedTabs, setAllowedTabs] = useState(() => {
    try {
      const stored = sessionStorage.getItem('gs_allowed_tabs');
      if (!stored) return ALL_TABS;
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return ALL_TABS;
      return parsed;
    } catch { return ALL_TABS; }
  });
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [whatsappEnabled, setWhatsappEnabled] = useState(
    () => sessionStorage.getItem('gs_whatsapp_enabled') === 'true'
  );
  const [lockedTabs, setLockedTabs] = useState(() => {
    try {
      const stored = sessionStorage.getItem('gs_locked_tabs');
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });

  // Register Web Push subscription when logged in and user has messages access
  usePushNotifications(loggedIn && canAccess(allowedTabs, 'messages'));

  const applySchoolLogoUrl = useCallback((url) => {
    const resolved = schoolLogoAPI.resolveUrl(url || '');
    if (resolved) sessionStorage.setItem('gs_school_logo_url', resolved);
    else sessionStorage.removeItem('gs_school_logo_url');
    setSchoolLogoUrl(resolved);
  }, []);

  const handleLogin = (token, schoolCodeFromForm, name, mustChange, tabs, location, tagline, contactNameVal, waEnabled, certsEnabled, bioEnabled, staffName, empBioEnabled, stuBioEnabled) => {
    // Use the normalised form value directly — the backend has already validated
    // and lowercased the school_code, so no client-side JWT decode is needed.
    const rawCode = (schoolCodeFromForm || '').trim().toLowerCase();
    const schoolCode = rawCode === 'admin' ? '__admin__' : rawCode;
    const resolvedTabs = Array.isArray(tabs) ? tabs : ALL_TABS;
    const initialLocked = [];
    if (!certsEnabled)   initialLocked.push('certificates');
    if (!bioEnabled)     initialLocked.push('biometric');
    if (!empBioEnabled)  initialLocked.push('employee-biometric');
    if (!stuBioEnabled)  initialLocked.push('student-biometric');
    if (!waEnabled)      initialLocked.push('whatsapp');
    sessionStorage.setItem('gs_token',        token);
    sessionStorage.setItem('gs_school_code',  schoolCode);
    sessionStorage.setItem('gs_school_name',  name || '');
    sessionStorage.setItem('gs_school_location', location || '');
    sessionStorage.setItem('gs_school_tagline',  tagline || '');
    sessionStorage.setItem('gs_contact_name',     contactNameVal || '');
    if (staffName) sessionStorage.setItem('gs_staff_full_name', staffName);
    else sessionStorage.removeItem('gs_staff_full_name');
    sessionStorage.setItem('gs_allowed_tabs', JSON.stringify(resolvedTabs));
    sessionStorage.setItem('gs_whatsapp_enabled', waEnabled ? 'true' : 'false');
    sessionStorage.setItem('gs_locked_tabs', JSON.stringify(initialLocked));
    sessionStorage.removeItem('gs_school_logo_url');
    setSchoolName(name || '');
    setSchoolLocation(location || '');
    setSchoolTagline(tagline || '');
    setSchoolLogoUrl('');
    setContactName(contactNameVal || '');
    setStaffFullName(staffName || '');
    setAllowedTabs(resolvedTabs);
    setLockedTabs(initialLocked);
    setWhatsappEnabled(!!waEnabled);
    if (mustChange) {
      setMustChangePassword(true);
      setLoggedIn(true);
    } else {
      setMustChangePassword(false);
      setLoggedIn(true);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('gs_token');
    sessionStorage.removeItem('gs_school_code');
    sessionStorage.removeItem('gs_school_name');
    sessionStorage.removeItem('gs_school_location');
    sessionStorage.removeItem('gs_school_tagline');
    sessionStorage.removeItem('gs_school_logo_url');
    sessionStorage.removeItem('gs_allowed_tabs');
    sessionStorage.removeItem('gs_whatsapp_enabled');
    sessionStorage.removeItem('gs_locked_tabs');
    sessionStorage.removeItem('gs_staff_full_name');
    localStorage.removeItem('gs_academic_year');
    setLockedTabs([]);
    setSchoolLogoUrl('');
    setStaffFullName('');
    setLoggedIn(false);
    setMustChangePassword(false);
  };

  useEffect(() => {
    if (!loggedIn || sessionStorage.getItem('gs_school_code') === '__admin__') return undefined;

    let cancelled = false;
    const loadLogo = () => {
      schoolLogoAPI.getCurrent()
        .then((res) => {
          if (cancelled) return;
          applySchoolLogoUrl(res.data?.data?.exists ? res.data?.data?.logo_url : '');
        })
        .catch(() => {
          // Keep the last known logo in place if a transient lookup fails.
        });
    };
    const handleLogoUpdated = (event) => {
      if (event.detail && Object.prototype.hasOwnProperty.call(event.detail, 'logoUrl')) {
        applySchoolLogoUrl(event.detail.logoUrl);
      }
      loadLogo();
    };

    loadLogo();
    window.addEventListener('school-logo-updated', handleLogoUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('school-logo-updated', handleLogoUpdated);
    };
  }, [loggedIn, applySchoolLogoUrl]);

  // Poll feature flags every 30s and on window focus — updates locked state without re-login
  useEffect(() => {
    if (!loggedIn || sessionStorage.getItem('gs_school_code') === '__admin__') return undefined;

    const applyFlags = (flags) => {
      const locked = [];
      if (!flags.certificates_enabled)          locked.push('certificates');
      if (!flags.biometric_enabled)              locked.push('biometric');
      if (!flags.employee_biometric_enabled)     locked.push('employee-biometric');
      if (!flags.student_biometric_enabled)      locked.push('student-biometric');
      if (!flags.whatsapp_enabled)               locked.push('whatsapp');
      setLockedTabs(locked);
      sessionStorage.setItem('gs_locked_tabs', JSON.stringify(locked));
      setWhatsappEnabled(!!flags.whatsapp_enabled);
      sessionStorage.setItem('gs_whatsapp_enabled', flags.whatsapp_enabled ? 'true' : 'false');
      if (flags.is_active === false) handleLogout();
    };

    const fetchFlags = () => {
      authAPI.getFeatureFlags().then(res => applyFlags(res.data)).catch((e) => console.warn('[feature-flags]', e?.response?.data || e.message));
    };

    fetchFlags();
    const interval = setInterval(fetchFlags, 5000);
    window.addEventListener('focus', fetchFlags);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', fetchFlags);
    };
  }, [loggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Public route: progress card (accessible without login via QR code)
  if (window.location.pathname.startsWith('/progress-card/')) {
    return <ProgressCard />;
  }

  // Public route: student fee info (accessible without login via QR code on receipt)
  if (window.location.pathname.startsWith('/student-info/')) {
    return <StudentFeeInfo />;
  }

  // Public route: parent dashboard
  if (window.location.pathname.startsWith('/parent/')) {
    return (
      <>
        <PwaInstallPrompt />
        <ParentDashboard />
      </>
    );
  }

  // Public route: driver login (school code + bus number + PIN)
  if (window.location.pathname === '/driver-login') {
    return <DriverLogin />;
  }

  // Public route: driver mode (mobile UI for bus drivers) — requires driver token
  if (window.location.pathname === '/driver-mode') {
    return <DriverMode />;
  }

  if (!loggedIn) {
    return (
      <>
        <PwaInstallPrompt />
        <Login onLogin={handleLogin} />
      </>
    );
  }

  // Force password change screen
  if (mustChangePassword) {
    return (
      <ChangePassword
        forced
        onSuccess={() => {
          handleLogout(); // clear session, force re-login with new password
        }}
      />
    );
  }

  // Super Admin view
  if (sessionStorage.getItem('gs_school_code') === '__admin__') {
    return <AdminPanel onLogout={handleLogout} />;
  }

  return (
    <YearProvider>
      <Router>
        <div className="app">
          <PwaInstallPrompt />
          <AppLayout
            onLogout={handleLogout}
            schoolName={schoolName}
            staffFullName={staffFullName}
            allowedTabs={allowedTabs}
            whatsappEnabled={whatsappEnabled}
            lockedTabs={lockedTabs}
            schoolLogoUrl={schoolLogoUrl}
          >
            <AnimatedRoutes
              allowedTabs={allowedTabs}
              whatsappEnabled={whatsappEnabled}
              lockedTabs={lockedTabs}
              schoolName={schoolName}
              schoolLocation={schoolLocation}
              schoolTagline={schoolTagline}
              contactName={contactName}
              onContactNameChange={(name) => { setContactName(name); sessionStorage.setItem('gs_contact_name', name); }}
              schoolLogoUrl={schoolLogoUrl}
            />
          </AppLayout>
        </div>
      </Router>
    </YearProvider>
  );
}
